import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { TranslationService } from 'src/app/core/services/translation/translation.service';
import { DialogService } from 'src/app/core/services/dialog/dialog.service';
import { ActivatedRoute, Router } from '@angular/router';
import { RoutesKeys } from 'src/app/utils/routes';
import { DetailedRegion } from 'src/app/shared/models/dtos/region/detailed-region';
import { UserService } from 'src/app/core/api/user/user.service';
import {
  RewardForm,
  RewardFormSteps,
} from 'src/app/shared/models/reward-form/reward-form';
import { FormGroup } from '@angular/forms';
import { ProductStepperComponent } from '../../molecules/product-stepper/product-stepper.component';
import { RewardService } from 'src/app/core/api/reward/reward.service';
import { RewardResponseDTO } from 'src/app/shared/models/dtos/responses/reward-response-dto/reward-response-dto';
import { SelectorData } from 'src/app/utils/selectorDataType';
import { ProductDTO } from 'src/app/shared/models/dtos/responses/product-dto/product-dto';
import { isEntryLevel } from 'src/app/shared/models/constants/vip-levels';
import { SnackBarService } from 'src/app/core/services/snackbar/snackbar.service';
import { selectableRewardTypes } from 'src/app/shared/models/constants/reward-type';
import { RewardRequestDTO } from 'src/app/shared/models/dtos/requests/reward-request-dto/reward-request-dto';
import { ExceptionsDTO } from 'src/app/shared/models/dtos/exceptions-dto/exceptions-dto';
import { distinctUntilChanged, map, merge } from 'rxjs';
import { allCountriesSelectedValidator } from 'src/app/shared/validators/registration-validators';
import { ProductService } from 'src/app/core/api/product/product.service';

const REWARD_DUPLICATED_CODE = 'RWD-DRN-001';
const REWARD_DUPLICATED_ENTRY_CODE = 'RWD-DEE-001';
const REWARD_DUPLICATED_ERROR_CODES = [
  REWARD_DUPLICATED_CODE,
  REWARD_DUPLICATED_ENTRY_CODE,
];

export interface RewardLabels {
  controlPanel: string;
  addReward: string;
  editReward: string;
  cancel: string;
  next: string;
  back: string;
  create: string;
  update: string;
  error: string;
  rewardDuplicatedError: string;
  editDisclaimer: string;
  addDisclaimer: string;
  step1Title: string;
  step2Title: string;
  step3Title: string;
  printerFamilyBasename: string;
}

@Component({
  selector: 'app-add-edit-reward',
  templateUrl: './add-edit-reward.component.html',
  styleUrls: ['./add-edit-reward.component.scss'],
})
/**
 * Component for adding and editing rewards.
 */
export class AddEditRewardComponent implements OnInit {
  @Input() previousPage?: RoutesKeys;
  @Input() product?: ProductDTO | null;
  @ViewChild('stepper') stepper!: ProductStepperComponent;
  rewardForm = RewardForm.createForm();

  regionList: DetailedRegion[] = [];
  rewardId?: number;
  loading = false;
  regionLoading = false;
  isEditing = false;
  relatedFamily: string = '';
  isStepValid = false;

  // Family and Business Level Data
  protected availableFamilies: any[] = [];
  familyMap: any = {};
  availableBusinessLevels: any = {};

  labels: RewardLabels = {
    controlPanel: 'control-panel',
    addReward: 'add-reward',
    editReward: 'edit-reward',
    cancel: 'cancel',
    next: 'next-button',
    back: 'back',
    create: 'create',
    update: 'update',
    error: 'try-again-later',
    rewardDuplicatedError: 'reward-duplicated-error',
    editDisclaimer: 'edit-reward-disclaimer',
    addDisclaimer: 'add-reward-disclaimer',
    step1Title: 'reward-form.step-1-title',
    step2Title: 'reward-form.step-2-title',
    step3Title: 'reward-form.step-3-title',
    printerFamilyBasename: 'business-series',
  };

  // prettier-ignore
  /**
   * Creates an instance of the AddEditRewardComponent.
   * @param {TranslationService} translationService - Service for handling translations
   * @param {Router} router - Angular router service
   * @param {DialogService} dialogService - Service for handling dialogs
   * @param {ActivatedRoute} activatedRoute - Service for accessing route parameters
   * @param {UserService} userService - Service for user management requests
   * @param {RewardService} rewardService - Service for reward requests
   * @param {SnackBarService} snackBarService - Service for displaying snackbar notifications
   */
  constructor( // NOSONAR
    private readonly translationService: TranslationService,
    private router: Router,
    private dialogService: DialogService,
    private activatedRoute: ActivatedRoute,
    private userService: UserService,
    private productService: ProductService,
    private rewardService: RewardService,
    private snackBarService: SnackBarService
  ) {}

  /**
   * ngOnInit
   */
  ngOnInit() {
    this.translationService.translateObject(this.labels);
    this.getFormRegions();

    this.rewardId = this.activatedRoute.snapshot.params['id'];

    this.productService.getAllFamilyProduct().subscribe((families) => {
      this.availableFamilies = families.map((family) => ({
        label: `${this.labels.printerFamilyBasename} ${family.name}`,
        value: family.id,
        family: family.name,
      }));
    });

    this.userService
      .getAllBusinessLevelsGroupedByFamily()
      .subscribe((familiesBusinessLevels) => {
        for (const levelByFamily of familiesBusinessLevels) {
          this.availableBusinessLevels[levelByFamily.familyName] =
            levelByFamily.businessLevels;
          for (const businessLevel of levelByFamily.businessLevels) {
            this.familyMap[businessLevel.id] = businessLevel.familyPk;
          }
        }
        this.setEditFields();
      });

    merge(this.rewardForm.valueChanges, this.rewardForm.statusChanges)
      .pipe(distinctUntilChanged())
      .subscribe(() => {
        this.updateCurrentStepValidity();
      });

    this.rewardForm.updateValueAndValidity();
    this.updateCurrentStepValidity();

    this.setProductRelatedFormFields();
  }

  /**
   * sets form fields in case a product related addition/edit was called
   */
  private setProductRelatedFormFields() {
    if (this.product && !this.rewardId) {
      const step1 = this.rewardForm.get('step1');
      step1
        ?.get('relatedProduct')
        ?.setValue(this.getRelatedProductFromObject(this.product));
      step1?.get('isRelatedToProduct')?.setValue(true);
      this.relatedFamily = this.product.family;
    }
  }

  /**
   * sets form fields in case of an edit routine
   */
  private setEditFields() {
    if (!this.rewardId) return;
    this.loading = true;
    this.rewardService.getReward(this.rewardId).subscribe({
      next: (reward: RewardResponseDTO) => this.applyRewardToForm(reward),
      error: () => {
        this.loading = false;
        this.snackBarService.error(this.labels.error);
      },
      complete: () => {
        this.loading = false;
      },
    });
    this.rewardForm.get('step1')?.get('isRelatedToProduct')?.disable();
    this.rewardForm.get('step1')?.get('rewardCode')?.disable();
  }

  /**
   * @param {RewardResponseDTO} reward
   */
  private applyRewardToForm(reward: RewardResponseDTO): void {
    this.relatedFamily = reward.product?.family ?? '';
    const vipLevels = this.getOnlyVipLevels(reward.businessLevelIds);
    const vipOnly = vipLevels.length === reward.businessLevelIds.length;

    this.rewardForm.get('step1')?.patchValue({
      isRelatedToProduct: !!reward.product,
      rewardType: this.getRewardTypeFromString(reward.rewardType),
      relatedProduct: this.getRelatedProductFromObject(reward.product),
      name: reward.rewardTitles.find((t) => t.language === 'en')?.name ?? null,
      namePt:
        reward.rewardTitles.find((t) => t.language === 'pt')?.name ?? null,
      nameEs:
        reward.rewardTitles.find((t) => t.language === 'es')?.name ?? null,
      productImage: reward.imgSource ?? null,
      rewardDiscount: reward.discount ?? null,
      rewardCost: reward.cost ?? null,
      redemptionLimit:
        reward.redemptionLimit > 0 ? reward.redemptionLimit : null,
      rewardCode: reward.rewardCode ?? null,
      extensionPeriod: reward.extensionPeriod ?? null,
      businessLevels: reward.businessLevelIds,
      vipLevels: vipOnly ? this.getOnlyVipLevels(reward.businessLevelIds) : [],
      rewardFamilies: RewardResponseDTO.getFamilyIds(
        this.familyMap,
        reward.businessLevelIds
      ),
    });
    this.rewardForm.get('step2')?.patchValue({
      regionExceptions: reward.rewardExceptions ?? null,
    });
    this.rewardForm.get('step3')?.patchValue({
      startDate: reward.startDate ? new Date(reward.startDate) : null,
      endDate: reward.endDate ? new Date(reward.endDate) : null,
    });
  }

  /**
   * Filter all business levels to get only the VIP levels
   * @param {number[]} allLevels
   * @return {number[]} a list withVIP levels
   */
  getOnlyVipLevels(allLevels: number[]): number[] {
    const vipLevels: number[] = [];

    allLevels.forEach((level) => {
      if (!isEntryLevel(level, this.availableBusinessLevels)) {
        vipLevels.push(level);
      }
    });

    return vipLevels;
  }

  /**
   * Gets the regions to be used within the form
   */
  private getFormRegions() {
    this.regionLoading = true;
    this.userService
      .listCountries()
      .pipe(
        map((regions) =>
          regions.map((region) => ({
            ...region,
            countries: region.countries.sort((a, b) =>
              a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
            ),
          }))
        )
      )
      .subscribe({
        next: (sortedRegions) => {
          this.regionList = sortedRegions;
          this.rewardForm
            .get('step3')
            ?.get('regionExceptions')
            ?.setValidators(allCountriesSelectedValidator(this.regionList));
          this.regionLoading = false;
        },
        error: () => {
          this.regionList = [];
          this.regionLoading = false;
        },
      });
  }

  /**
   * Retrieves the related product from the reward object.
   * @param {ProductDTO} associatedProduct - The associated product.
   * @return {SelectorData | null} - The related product.
   */
  private getRelatedProductFromObject(
    associatedProduct: ProductDTO
  ): SelectorData | null {
    if (associatedProduct) {
      return {
        label: associatedProduct.name,
        value: associatedProduct.id,
      } as SelectorData;
    }
    return null;
  }

  /**
   * Retrieves the reward type from the reward object.
   * @param {string} rewardType - The reward type.
   * @return {SelectorData | null} - The reward type.
   */
  private getRewardTypeFromString(rewardType: string): SelectorData | null {
    return (
      selectableRewardTypes.find(
        (type: SelectorData) => type.value === rewardType
      ) || null
    );
  }

  /**
   * Navigates back to the control panel.
   * This is called when the user clicks the cancel button.
   */
  goBack() {
    this.previousPage
      ? this.router.navigate([this.previousPage])
      : this.router.navigate([RoutesKeys.CONTROL_PANEL]);
  }

  /**
   * handles the click to execute add/edit action.
   */
  handleSendReward() {
    if (this.rewardForm.invalid) {
      return;
    }

    const outboundRewardDTO = this.assembleRewardDTO();

    if (this.rewardId) {
      this.editReward(this.rewardId, outboundRewardDTO);
    } else {
      this.saveReward(outboundRewardDTO);
    }
  }

  /**
   * assembles the outbound DTO for add/edit requests from the rewardForm
   * @return {RewardRequestDTO}
   */
  assembleRewardDTO(): RewardRequestDTO {
    const { step1, step2, step3 } = this.rewardForm.controls;
    const {
      isRelatedToProduct,
      rewardType,
      relatedProduct,
      name,
      namePt,
      nameEs,
      productImage,
      redemptionLimit,
      rewardDiscount,
      rewardCost,
      rewardCode,
      extensionPeriod,
      vipLevels,
      rewardFamilies,
    } = step1.controls;
    const { regionExceptions } = step2.controls;
    const { startDate, endDate } = step3.controls;

    let rewardTitles = null;

    if (rewardFamilies.value?.length === 0) {
      vipLevels.setValue(null);
    }

    if (!isRelatedToProduct.value) {
      rewardTitles = RewardRequestDTO.formatRewardTitles(
        name.value!,
        namePt.value!,
        nameEs.value!
      );
    }

    return new RewardRequestDTO(
      rewardCost.value!,
      productImage.value ?? '',
      rewardDiscount.value!,
      rewardType.value?.value,
      vipLevels.value ?? [],
      rewardTitles,
      startDate.value!,
      endDate.value ?? null,
      redemptionLimit.value ?? 0,
      extensionPeriod.value!,
      isRelatedToProduct.value ?? false,
      rewardFamilies.value ?? [],
      rewardCode.value!,
      ExceptionsDTO.selectedCountryIds(regionExceptions.value ?? []),
      relatedProduct.value?.value ?? null
    );
  }

  /**
   * Save reward service call
   * @param {RewardRequestDTO} rewardDTO
   */
  saveReward(rewardDTO: RewardRequestDTO) {
    if (this.loading) return;
    this.loading = true;
    this.rewardService.saveReward(rewardDTO).subscribe({
      next: () => {
        const dialogData = {};
        this.dialogService.openRewardCreatedModal(
          dialogData,
          () => this.router.navigate([RoutesKeys.REWARDS]),
          () => {
            this.router.navigate([RoutesKeys.CONTROL_PANEL]);
          }
        );
      },
      error: (err: any) => {
        // eslint-disable-next-line no-prototype-builtins
        let errorSnackMessage: string = '';
        if (err.error.errorCode === REWARD_DUPLICATED_CODE) {
          errorSnackMessage = this.labels.rewardDuplicatedError;
        } else {
          errorSnackMessage =
            err.status === 400 ? err.error.errorMessage : this.labels.error;
        }

        this.loading = false;
        this.snackBarService.error(errorSnackMessage);
      },
      complete: () => {
        this.loading = false;
      },
    });
  }

  /**
   * Edit reward service call
   * @param {number} rewardId
   * @param {RewardRequestDTO} rewardDTO
   */
  editReward(rewardId: number, rewardDTO: RewardRequestDTO) {
    if (this.loading) return;
    this.loading = true;
    this.rewardService.editReward(rewardId, rewardDTO).subscribe({
      next: () => {
        this.goBack();
      },
      error: (err: any) => {
        let errorSnackMessage: string = '';
        if (err.error.errorCode === REWARD_DUPLICATED_CODE) {
          errorSnackMessage = this.labels.rewardDuplicatedError;
        } else {
          errorSnackMessage =
            err.status === 400 ? err.error.errorMessage : this.labels.error;
        }

        this.loading = false;
        this.snackBarService.error(errorSnackMessage);
      },
      complete: () => {
        this.loading = false;
        this.snackBarService.success('Reward edited Successfully');
      },
    });
  }

  /**
   * Handles error from reward save request
   * @param {any} err http error response
   */
  private handleRewardSaveError(err: any): void {
    // eslint-disable-next-line no-prototype-builtins
    let errorSnackMessage: string = '';
    if (REWARD_DUPLICATED_ERROR_CODES.includes(err.error.errorCode)) {
      errorSnackMessage = this.labels.rewardDuplicatedError;
    } else {
      errorSnackMessage =
        err.status === 400 ? err.error.errorMessage : this.labels.error;
    }
    this.loading = false;
    this.snackBarService.error(errorSnackMessage);
  }

  /**
   * Updates the editing state of the component.
   * @param {boolean} editing
   */
  onEditing(editing: boolean): void {
    this.isEditing = editing;
  }

  /**
   * Retrieves the form group for the specified step.
   * @param {number} step - The step number.
   * @return {FormGroup} - The form group for the given step.
   */
  getForm(step: number): FormGroup {
    switch (step) {
      case 1:
        return this.rewardForm.get('step1') as FormGroup;
      case 2:
        return this.rewardForm.get('step2') as FormGroup;
      case 3:
        return this.rewardForm.get('step3') as FormGroup;
      default:
        return this.rewardForm.get('step1') as FormGroup;
    }
  }

  /**
   * Updates current step validity
   */
  private updateCurrentStepValidity(): void {
    const stepKey = `step${
      this.stepper?.currentStep + 1
    }` as keyof RewardFormSteps;
    const currentGroup = this.rewardForm.get(stepKey) as FormGroup;
    if (currentGroup) {
      setTimeout(() => {
        this.isStepValid = currentGroup.valid;
      });
    }
  }
}
